import { asStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TenantRosterUser } from "@bezoned/shared/types/api/tenant/tenant-roster-get.types.js";
import { Button, PresenceBadge } from "@fluentui/react-components";
import type { PresenceBadgeStatus } from "@fluentui/react-components";
import type { ColumnDef } from "@tanstack/react-table";

import { RosterLocalTime } from "./roster-local-time.js";
import { RosterSwitchOfficeButton } from "./roster-switch-office-button.js";
import {
  compareRosterUsersByPresenceAndLastName,
  getLocalTimeSortValue,
  getRosterName,
} from "./roster-table-utils.js";

type CurrentTeam = NonNullable<TenantRosterUser["currentTeam"]>;

interface GetRosterColumnsOptions {
  currentTeamId: string | undefined;
  currentTime: Date;
  onOfficeClick: (args: {
    teamId: CurrentTeam["id"];
    teamName: CurrentTeam["name"];
  }) => void;
  onUserClick: (args: { msUserId: string; anchorRect: DOMRect }) => void;
  tickCounter: number;
}

export function getRosterColumns({
  currentTeamId,
  currentTime,
  onOfficeClick,
  onUserClick,
  tickCounter,
}: GetRosterColumnsOptions): ColumnDef<TenantRosterUser, unknown>[] {
  return [
    {
      accessorFn: getRosterName,
      cell: ({ row }) => {
        const user = row.original;
        const presenceStatus = user.availability as PresenceBadgeStatus | null;

        return (
          <Button
            appearance="transparent"
            className="hover:text-bzd-purple-500 max-w-full justify-start px-0 text-left"
            icon={
              presenceStatus ? (
                <PresenceBadge
                  className="shrink-0"
                  outOfOffice={user.outOfOffice?.isOutOfOffice ?? false}
                  status={presenceStatus}
                />
              ) : null
            }
            onClick={asStableCallback((event) => {
              event.stopPropagation();
              onUserClick({
                anchorRect: event.currentTarget.getBoundingClientRect(),
                msUserId: user.msUserId,
              });
            })}
            type="button"
          >
            <span className="truncate hover:underline">
              {getRosterName(user)}
            </span>
          </Button>
        );
      },
      enableGlobalFilter: true,
      header: "Name",
      id: "name",
      sortingFn: (rowA, rowB) =>
        compareRosterUsersByPresenceAndLastName(rowA.original, rowB.original),
    },
    {
      accessorFn: (user) => user.statusMessage?.trim() || undefined,
      cell: ({ row }) => (
        <span
          className="block truncate"
          title={row.original.statusMessage ?? ""}
        >
          {row.original.statusMessage ?? ""}
        </span>
      ),
      enableGlobalFilter: false,
      header: "Status",
      id: "status",
      sortUndefined: "last",
    },
    {
      accessorFn: (user) => user.currentTeam?.name ?? undefined,
      cell: ({ row }) => (
        <RosterSwitchOfficeButton
          currentTeamId={currentTeamId}
          onOfficeClick={onOfficeClick}
          team={row.original.currentTeam}
        />
      ),
      enableGlobalFilter: false,
      header: "BeZoned office",
      id: "bezonedLocation",
      sortUndefined: "last",
    },
    {
      accessorFn: (user) => user.workLocation?.trim() || undefined,
      cell: ({ row }) => <span>{row.original.workLocation ?? "—"}</span>,
      enableGlobalFilter: false,
      header: "Work location",
      id: "workLocation",
      sortUndefined: "last",
    },
    {
      accessorFn: (user) => getLocalTimeSortValue(user.timeZone, currentTime),
      cell: ({ row }) =>
        row.original.timeZone === null ? (
          <span>—</span>
        ) : (
          <RosterLocalTime
            tickCounter={tickCounter}
            timeZone={row.original.timeZone}
          />
        ),
      enableGlobalFilter: false,
      header: "Local time",
      id: "localTime",
      sortUndefined: "last",
    },
  ];
}
